import React, { useState } from 'react';
import styled from 'styled-components';

import { useOrdersQueriesInvalidator } from '#/api/queries/orderQueries';
import ToggleSlider from '#/components/basic/inputs/ToggleSlider/ToggleSlider';
import ConfirmationModal from '#/components/basic/modal/ConfirmationModal';
import { skipCancelOrderConfirmation } from '#/reduxState/exchange/userGuideReducer';
import { useSelector, staticDispatch } from '#/reduxState/store';

import {
  cancelOrderWithSignModal,
  displayCancelErrorModal,
} from './orderCancelUtils';

const DoNotAsk = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;

function CancelOrderModal({
  onCancel,
  onClose,
  processing,
}: {
  onCancel: () => void;
  onClose: () => void;
  processing: boolean;
}) {
  const [remember, setRemember] = useState(false);

  return (
    <ConfirmationModal
      onClose={onClose}
      onConfirm={() => {
        if (remember) {
          staticDispatch(skipCancelOrderConfirmation());
        }
        onCancel();
      }}
      processing={processing}
      confirmText="Cancel Order"
      title="Cancel Order"
      confirmButtonProps={{
        bordered: true,
        color: 'red',
        autoFocus: true,
      }}
      childrenBottom={
        <DoNotAsk>
          Do not ask me again{' '}
          <ToggleSlider onChange={setRemember} checked={remember} />
        </DoNotAsk>
      }
    >
      <span style={{ color: 'var(--gray4c)' }}>
        Your order will be canceled.
      </span>
    </ConfirmationModal>
  );
}

export function useCancelOrder() {
  const { removeOrderFromCachedQuery } = useOrdersQueriesInvalidator();
  const skipCancelOrderConfirm = useSelector(
    state => state.exchange.userGuide.skipCancelOrderConfirm
  );
  const wallet = useSelector(state => state.wallet);
  const delegatedKey = useSelector(state => state.delegatedKey.authorization);

  const [cancelConfirmationOrderId, setCancelConfirmationOrderId] =
    useState<string>();
  const [cancelingOrderId, setCancelingOrderId] = useState<string>();

  const handleCancelOrder = async (orderId: string) => {
    if (!skipCancelOrderConfirm && !cancelConfirmationOrderId) {
      // Extra step, user must confirm fist
      setCancelConfirmationOrderId(orderId);
      return;
    }
    setCancelingOrderId(orderId);
    return cancelOrderWithSignModal(orderId, wallet, delegatedKey)
      .then(() => {
        setCancelConfirmationOrderId(undefined);
        setCancelingOrderId(undefined);
        // Update UI immediatelly (without waiting for WebSocket)
        removeOrderFromCachedQuery(orderId);
      })
      .catch(error => {
        setCancelConfirmationOrderId(undefined);
        setCancelingOrderId(undefined);
        displayCancelErrorModal(error);
      });
  };

  // We rely on WebSocket to proceed (and close Modal)
  // as also Table itself us updated by it.
  // useWebsocketEventListener('orders', event => {
  //   if (event.data.type === OrderEventType.liquidation ||
  //     event.data.type === OrderEventType.deleverage) { return; }
  //   if (isOrderInactive(event.data.status) &&
  //     event.data.orderId === cancelingOrderId) {
  //     setCancelingOrderId(undefined);
  //     setCancelConfirmationOrderId(undefined);
  //   }
  // });

  return {
    cancelingOrderId,
    handleCancelOrder,
    renderCancelation: () => (
      <>
        {cancelConfirmationOrderId && (
          <CancelOrderModal
            onCancel={() => handleCancelOrder(cancelConfirmationOrderId)}
            onClose={() => setCancelConfirmationOrderId(undefined)}
            processing={!!cancelingOrderId}
          />
        )}
      </>
    ),
  };
}
